import React, {useState} from 'react';
import {
  Box,
  Text,
  HStack,
  Pressable,
  Checkbox,
  ScrollView,
  VStack,
} from 'native-base';
import {createIconSetFromFontello} from 'react-native-vector-icons';
import lineAwesomeConfig from '../../Assets/Fontello/line-awesome-config.json';

const Icon = createIconSetFromFontello(lineAwesomeConfig);

/**
 * MultiSelectFilter - A tag/chip style multi-select dropdown
 *
 * @param {string} label - Label shown above the input
 * @param {string} placeholder - Placeholder text for the input
 * @param {Array} options - Array of {id, name} objects
 * @param {Array} selectedValues - Array of selected IDs (strings)
 * @param {Function} onChange - Callback with updated array of selected IDs
 */
const MultiSelectFilter = ({
  label,
  placeholder = 'Select...',
  options = [],
  selectedValues = [],
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = id => {
    const stringId = id.toString();
    if (selectedValues.includes(stringId)) {
      onChange(selectedValues.filter(v => v !== stringId));
    } else {
      onChange([...selectedValues, stringId]);
    }
  };

  const handleRemove = id => {
    const stringId = id.toString();
    onChange(selectedValues.filter(v => v !== stringId));
  };

  const getSelectedNames = () => {
    return options
      .filter(opt => selectedValues.includes(opt.id.toString()))
      .map(opt => ({id: opt.id, name: opt.name}));
  };

  return (
    <VStack space={1}>
      {label && (
        <Text fontSize="sm" fontWeight="500" color="gray.600">
          {label}
        </Text>
      )}

      {/* Dropdown trigger */}
      <Pressable onPress={() => setIsOpen(!isOpen)}>
        <Box
          borderWidth={1}
          borderColor={isOpen ? 'primary.500' : 'gray.300'}
          borderRadius="md"
          px={3}
          py={2}
          bg="white"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center">
          <Text color={selectedValues.length > 0 ? 'gray.800' : 'gray.400'}>
            {selectedValues.length > 0
              ? `${selectedValues.length} selected`
              : placeholder}
          </Text>
          <Icon
            name={isOpen ? 'angle-up' : 'angle-down'}
            size={16}
            color="#666"
          />
        </Box>
      </Pressable>

      {/* Dropdown menu */}
      {isOpen && (
        <Box
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="md"
          bg="white"
          shadow={2}
          maxH={200}
          zIndex={1000}>
          <ScrollView>
            {options.length === 0 ? (
              <Box px={3} py={2}>
                <Text color="gray.400" fontSize="sm">
                  No options available
                </Text>
              </Box>
            ) : (
              options.map(option => (
                <Pressable
                  key={option.id}
                  onPress={() => handleToggle(option.id)}
                  _pressed={{bg: 'gray.100'}}>
                  <HStack
                    px={3}
                    py={2}
                    space={2}
                    alignItems="center"
                    bg={
                      selectedValues.includes(option.id.toString())
                        ? 'primary.50'
                        : 'transparent'
                    }>
                    <Checkbox
                      value={option.id.toString()}
                      isChecked={selectedValues.includes(option.id.toString())}
                      isReadOnly
                      colorScheme="primary"
                      size="sm"
                      accessibilityLabel={option.name}
                      aria-label={option.name}
                    />
                    <Text fontSize="sm" flex={1} numberOfLines={1}>
                      {option.name}
                    </Text>
                  </HStack>
                </Pressable>
              ))
            )}
          </ScrollView>
        </Box>
      )}

      {/* Selected tags */}
      {selectedValues.length > 0 && (
        <HStack flexWrap="wrap" space={1} mt={1}>
          {getSelectedNames().map(item => (
            <Pressable key={item.id} onPress={() => handleRemove(item.id)}>
              <HStack
                bg="primary.100"
                borderRadius="full"
                px={2}
                py={1}
                space={1}
                alignItems="center"
                mb={1}>
                <Text fontSize="xs" color="primary.700" numberOfLines={1}>
                  {item.name}
                </Text>
                <Icon name="times" size={12} color="#0369a1" />
              </HStack>
            </Pressable>
          ))}
        </HStack>
      )}
    </VStack>
  );
};

export default MultiSelectFilter;
